// Live export preview.
//
// Renders the resolved section blocks (real data, from exportData.js) in the
// visual idiom of the chosen format, so the customer sees exactly what they
// are assembling before they download. Block renderers are shared across the
// PDF (paper) and PowerPoint (slide) previews; CSV and Excel get their own
// data-shaped renderers.
//
// Calculations: none. Tables are capped to a handful of rows for the preview
// with a "+N more" note; the generated file follows the same contract.
import React from 'react';
import { Box, Typography } from '@mui/material';
import { buildWorkbookModel, csvFilesModel } from '../exportGenerators';

const PREVIEW_ROWS = 6;

// -- Shared block renderers -------------------------------------------------
function BlockGauge({ block }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography sx={{ fontSize: 48, fontWeight: 800, color: 'error.main', lineHeight: 1, fontFamily: 'system-ui' }}>{block.value}</Typography>
            <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary' }}>{block.band}</Typography>
                <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>{block.sub}</Typography>
            </Box>
        </Box>
    );
}

function BlockMetrics({ block }) {
    // `dense` (set by the slide preview) compresses the tiles so all four
    // metrics fit a 16:9 slide thumbnail instead of clipping to the first row.
    const dense = !!block.dense;
    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: dense ? 0.5 : 1 }}>
            {block.items.map((m) => (
                <Box key={m.label} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: dense ? 0.5 : 1 }}>
                    <Typography sx={{ fontSize: dense ? 13 : 18, fontWeight: 800, color: 'text.primary', lineHeight: 1, fontFamily: 'system-ui' }}>{m.value}</Typography>
                    <Typography sx={{ fontSize: dense ? 8.5 : 10, fontWeight: 600, color: 'text.primary', mt: 0.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.label}</Typography>
                    {!dense && <Typography sx={{ fontSize: 9, color: 'text.disabled' }}>{m.sub}</Typography>}
                </Box>
            ))}
        </Box>
    );
}

// Bars match the portal's .brow/.btrack/.bfill style: 7px track, 3px radius,
// var(--bar-track) track and var(--teal) fill (auto theme-aware via the tokens
// on .rbx-root), muted label and dim right-aligned value.
function BlockBars({ block }) {
    const max = Math.max(1, ...block.items.map((b) => (b.max != null ? b.max : Number(b.value) || 0)));
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {block.items.map((b) => {
                const wpct = Math.max(0, Math.min(100, Math.round((Number(b.value) || 0) / (b.max || max) * 100)));
                return (
                    <Box key={b.label} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Typography sx={{ fontSize: 11, color: 'var(--muted)', width: 90, flexShrink: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.label}</Typography>
                        <Box sx={{ flex: 1, height: '7px', background: 'var(--bar-track)', borderRadius: '3px', overflow: 'hidden' }}>
                            <Box sx={{ height: '7px', borderRadius: '3px', width: `${wpct}%`, background: 'var(--teal)' }} />
                        </Box>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'var(--dim)', minWidth: 28, textAlign: 'right', flexShrink: 0 }}>{b.value}</Typography>
                    </Box>
                );
            })}
        </Box>
    );
}

// Smooth filled area over the series - the export-format cousin of the
// portal's trend charts. Pure SVG, sized by its container.
function BlockArea({ block }) {
    const items = block.items || [];
    if (!items.length) return null;
    const W = 560, H = 120;
    const max = Math.max(1, block.max || 0, ...items.map((b) => Number(b.value) || 0));
    const step = items.length > 1 ? W / (items.length - 1) : W;
    const pts = items.map((b, i) => `${(i * step).toFixed(1)},${(H - ((Number(b.value) || 0) / max) * H).toFixed(1)}`).join(' ');
    return (
        <Box>
            <Box component="svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" sx={{ width: '100%', height: 90, display: 'block' }}>
                <polygon points={`0,${H} ${pts} ${W},${H}`} fill="var(--teal)" opacity={0.14} />
                <polyline points={pts} fill="none" stroke="var(--teal)" strokeWidth="2.5" />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.25 }}>
                <Typography sx={{ fontSize: 9.5, color: 'text.disabled' }}>{items[0].label}</Typography>
                <Typography sx={{ fontSize: 9.5, color: 'text.disabled' }}>{items[items.length - 1].label}</Typography>
            </Box>
        </Box>
    );
}

function BlockTable({ block }) {
    const rows = block.rows.slice(0, PREVIEW_ROWS);
    return (
        <Box sx={{ fontFamily: 'system-ui' }}>
            {/* tableLayout:'fixed' divides width evenly across columns regardless
                of content, so short cells no longer collapse their column; each
                cell keeps an ellipsis so long values still truncate cleanly. */}
            <Box component="table" sx={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: 10 }}>
                <Box component="thead">
                    <Box component="tr">
                        {block.columns.map((c) => (
                            <Box key={c} component="th" sx={{ textAlign: 'left', fontSize: 8.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'text.disabled', borderBottom: '2px solid', borderColor: 'divider', px: 0.75, py: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c}</Box>
                        ))}
                    </Box>
                </Box>
                <Box component="tbody">
                    {rows.map((r, i) => (
                        <Box component="tr" key={i}>
                            {r.map((cell, j) => (
                                <Box key={j} component="td" sx={{ px: 0.75, py: 0.5, borderBottom: '1px solid', borderColor: 'divider', color: j === 0 ? 'text.primary' : 'text.secondary', fontWeight: j === 0 ? 600 : 400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cell}</Box>
                            ))}
                        </Box>
                    ))}
                </Box>
            </Box>
            {block.total > rows.length && (
                <Typography sx={{ fontSize: 9, color: 'text.disabled', mt: 0.5, fontStyle: 'italic' }}>Showing {rows.length} of {block.total} rows</Typography>
            )}
        </Box>
    );
}

function BlockNarrative({ block }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {block.paragraphs.map((p, i) => (
                <Box key={i}>
                    {p.h && <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.primary' }}>{p.h}</Typography>}
                    <Typography sx={{ fontSize: 10, color: 'text.secondary', lineHeight: 1.5 }}>{p.body}</Typography>
                    {p.action && <Typography sx={{ fontSize: 9.5, color: 'secondary.main', fontStyle: 'italic' }}>→ {p.action}</Typography>}
                </Box>
            ))}
        </Box>
    );
}

export function PreviewBlock({ block }) {
    if (!block) return null;
    if (block.kind === 'gauge')     return <BlockGauge block={block} />;
    if (block.kind === 'metrics')   return <BlockMetrics block={block} />;
    if (block.kind === 'bars')      return <BlockBars block={block} />;
    if (block.kind === 'area')      return <BlockArea block={block} />;
    if (block.kind === 'table')     return <BlockTable block={block} />;
    return <BlockNarrative block={block} />;
}

// -- Format previews --------------------------------------------------------
function PdfPreview({ title, sections, brand }) {
    return (
        <Box sx={{ width: '100%', maxWidth: 620, bgcolor: 'background.paper', boxShadow: '0 8px 24px rgba(0,0,0,0.08)', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 4 }}>
            <Box sx={{ borderBottom: '2px solid', borderColor: 'text.primary', pb: 1.25, mb: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <Typography sx={{ fontSize: 18, fontWeight: 800, color: 'text.primary' }}>{title || 'Untitled report'}</Typography>
                <Typography sx={{ fontSize: 9, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{brand || 'Boxphish'}</Typography>
            </Box>
            {sections.map((s, i) => (
                <Box key={s.id} sx={{ mb: 2.5 }}>
                    <Typography sx={{ fontSize: 9, fontWeight: 700, color: 'text.disabled', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                        Section {i + 1} · {s.group}{s.withDrill ? ' · with drill-down' : ''}
                    </Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary', mb: 0.75 }}>{s.label}</Typography>
                    <PreviewBlock block={s.block} />
                </Box>
            ))}
            <Typography sx={{ fontSize: 9, color: 'text.disabled', textAlign: 'center', mt: 3, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>Confidential · Page 1</Typography>
        </Box>
    );
}

// A 16:9 tile fits far less than a paper page, and overflow:hidden used to
// clip tables/bars mid-row (and rendering every full block froze the tab on
// big selections). Trim each block to what a tile can honestly show and say
// how much more the real deck carries.
function trimForSlide(block) {
    if (!block) return { block, extra: 0 };
    if (block.kind === 'metrics') {
        return { block: { ...block, dense: true }, extra: 0 };
    }
    if (block.kind === 'table') {
        const total = block.total ?? block.rows.length;
        return { block: { ...block, rows: block.rows.slice(0, 3), total }, extra: 0 };
    }
    if (block.kind === 'bars' && block.items.length > 5) {
        return { block: { ...block, items: block.items.slice(0, 5) }, extra: block.items.length - 5 };
    }
    if (block.kind === 'narrative' && block.paragraphs.length > 2) {
        return { block: { ...block, paragraphs: block.paragraphs.slice(0, 2) }, extra: block.paragraphs.length - 2 };
    }
    return { block, extra: 0 };
}

function PowerPointPreview({ title, sections, brand }) {
    const slides = [{ id: 'cover', label: title || 'Untitled report', cover: true }, ...sections];
    return (
        <Box sx={{ width: '100%', maxWidth: 780, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 1.5 }}>
            {slides.map((s, i) => (
                <Box key={s.id || i} sx={{ aspectRatio: '16 / 9', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.25, display: 'flex', flexDirection: 'column', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                    <Typography sx={{ fontSize: 8, color: 'text.disabled', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                        {s.cover ? 'Cover' : `Slide ${i} · ${s.group}`}
                    </Typography>
                    {s.cover ? (
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                            <Typography sx={{ fontSize: 16, fontWeight: 800, color: 'text.primary' }}>{s.label}</Typography>
                            <Typography sx={{ fontSize: 9, color: 'text.secondary', mt: 0.5 }}>{brand || 'Boxphish'}</Typography>
                        </Box>
                    ) : (
                        <>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.primary', mt: 0.25, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={s.label}>{s.label}</Typography>
                            {(() => {
                                const { block, extra } = trimForSlide(s.block);
                                return (
                                    <Box sx={{ flex: 1, mt: 0.75, overflow: 'hidden', transform: 'scale(0.92)', transformOrigin: 'top left' }}>
                                        <PreviewBlock block={block} />
                                        {extra > 0 && (
                                            <Typography sx={{ fontSize: 8.5, color: 'text.disabled', fontStyle: 'italic', mt: 0.25 }}>+{extra} more in the deck</Typography>
                                        )}
                                    </Box>
                                );
                            })()}
                        </>
                    )}
                </Box>
            ))}
        </Box>
    );
}

// Spreadsheet-style grid over [{ name, aoa }] models with clickable tabs.
// Shared by the Excel preview (workbook sheets) and the CSV preview (zip
// entries), so each preview shows exactly the cells the file will contain.
function GridPreview({ filename, sheets, countLabel }) {
    const [active, setActive] = React.useState(0);
    const sheet = sheets[Math.min(active, sheets.length - 1)];
    const rows = sheet.aoa.slice(0, 24);
    const ncol = Math.max(1, ...rows.map((r) => r.length));
    const colName = (i) => (i < 26 ? String.fromCharCode(65 + i) : `A${String.fromCharCode(39 + i)}`);
    const cellSx = { border: '1px solid', borderColor: 'divider', px: 0.75, py: 0.4, fontSize: 10, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160 };
    return (
        <Box sx={{ width: '100%', maxWidth: 760, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', bgcolor: 'action.hover' }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.primary' }}>{filename}</Typography>
                <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>{countLabel}</Typography>
            </Box>
            <Box sx={{ p: 2, overflowX: 'auto' }}>
                <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'auto', fontFamily: 'system-ui' }}>
                    <Box component="thead">
                        <Box component="tr">
                            <Box component="th" sx={{ ...cellSx, width: 28, bgcolor: 'action.hover', color: 'text.disabled', fontWeight: 600 }} />
                            {Array.from({ length: ncol }, (_, i) => (
                                <Box key={i} component="th" sx={{ ...cellSx, bgcolor: 'action.hover', color: 'text.disabled', fontWeight: 600, textAlign: 'center' }}>{colName(i)}</Box>
                            ))}
                        </Box>
                    </Box>
                    <Box component="tbody">
                        {rows.map((r, ri) => (
                            <Box component="tr" key={ri}>
                                <Box component="td" sx={{ ...cellSx, bgcolor: 'action.hover', color: 'text.disabled', textAlign: 'center' }}>{ri + 1}</Box>
                                {Array.from({ length: ncol }, (_, ci) => (
                                    <Box key={ci} component="td" sx={{ ...cellSx, color: ri === 0 ? 'text.primary' : 'text.secondary', fontWeight: ri === 0 ? 600 : 400 }}>{r[ci] != null ? String(r[ci]) : ''}</Box>
                                ))}
                            </Box>
                        ))}
                    </Box>
                </Box>
                {sheet.aoa.length > rows.length && (
                    <Typography sx={{ fontSize: 9, color: 'text.disabled', mt: 0.5, fontStyle: 'italic' }}>Showing {rows.length} of {sheet.aoa.length} rows</Typography>
                )}
            </Box>
            <Box sx={{ display: 'flex', gap: 0, borderTop: '1px solid', borderColor: 'divider', overflowX: 'auto', bgcolor: 'action.hover' }}>
                {sheets.map((sh, i) => (
                    <Box
                        key={i} onClick={() => setActive(i)} role="tab" tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActive(i); } }}
                        sx={{
                            px: 1.25, py: 0.6, fontSize: 10, cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none',
                            borderRight: '1px solid', borderColor: 'divider',
                            bgcolor: i === active ? 'background.paper' : 'transparent',
                            color: i === active ? 'text.primary' : 'text.secondary',
                            fontWeight: i === active ? 700 : 400,
                        }}
                    >{sh.name}</Box>
                ))}
            </Box>
        </Box>
    );
}

function ExcelPreview({ title, sections, brand, periodLabel, scopeLabel }) {
    const sheets = buildWorkbookModel(title, sections, { brand, periodLabel, scopeLabel });
    const base = (title || 'export').replace(/\s+/g, '-').toLowerCase();
    return <GridPreview filename={`${base}.xlsx`} sheets={sheets} countLabel={`${sheets.length} sheet${sheets.length === 1 ? '' : 's'}`} />;
}

// The CSV document downloads as a zip of real one-table CSV files; the tabs
// here are the files inside it.
function CsvPreview({ title, sections, brand, periodLabel, scopeLabel }) {
    const files = csvFilesModel(title, sections, { brand, periodLabel, scopeLabel });
    const base = (title || 'export').replace(/\s+/g, '-').toLowerCase();
    return <GridPreview filename={`${base}.zip`} sheets={files} countLabel={`${files.length} CSV file${files.length === 1 ? '' : 's'}`} />;
}

export default function ExportPreview({ format, title, sections, brand, periodLabel, scopeLabel }) {
    if (sections.length === 0) {
        return (
            <Box sx={{ width: '100%', maxWidth: 720, p: 6, bgcolor: 'background.paper', border: '1px dashed', borderColor: 'divider', borderRadius: 2, textAlign: 'center' }}>
                <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>Select components on the left to see a live preview of the data.</Typography>
            </Box>
        );
    }
    if (format === 'PDF')        return <PdfPreview title={title} sections={sections} brand={brand} />;
    if (format === 'PowerPoint') return <PowerPointPreview title={title} sections={sections} brand={brand} />;
    if (format === 'CSV')        return <CsvPreview title={title} sections={sections} brand={brand} periodLabel={periodLabel} scopeLabel={scopeLabel} />;
    return <ExcelPreview title={title} sections={sections} brand={brand} periodLabel={periodLabel} scopeLabel={scopeLabel} />;
}
